import { useState } from 'react';
import { CalendarIcon, ChevronDown, GitCompare } from 'lucide-react';
import { format } from 'date-fns';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { PeriodPreset, DateRange } from '../types';
import { PERIOD_PRESETS, formatDateRange } from '../utils/periodUtils';

interface PeriodSelectorProps {
  preset: PeriodPreset;
  customRange: DateRange | undefined;
  currentRange: DateRange;
  previousRange: DateRange;
  comparisonEnabled: boolean;
  onPresetChange: (preset: PeriodPreset) => void;
  onCustomRangeChange: (range: DateRange | undefined) => void;
  onComparisonChange: (enabled: boolean) => void;
  className?: string;
}

export function PeriodSelector({
  preset,
  customRange,
  currentRange,
  previousRange,
  comparisonEnabled,
  onPresetChange,
  onCustomRangeChange,
  onComparisonChange,
  className,
}: PeriodSelectorProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Convert our DateRange to react-day-picker's DateRange format
  const selectedRange: DayPickerDateRange | undefined = customRange
    ? { from: customRange.from, to: customRange.to }
    : undefined;

  const handleRangeSelect = (range: DayPickerDateRange | undefined) => {
    if (range?.from && range?.to) {
      onCustomRangeChange({ from: range.from, to: range.to });
      // Automatically switch to custom preset when selecting dates
      if (preset !== 'custom') {
        onPresetChange('custom');
      }
    } else if (range?.from) {
      // Partial selection - keep calendar open
      onCustomRangeChange({ from: range.from, to: range.from });
    }
  };

  const handlePresetChange = (value: string) => {
    onPresetChange(value as PeriodPreset);
    if (value !== 'custom') {
      setCalendarOpen(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Period Preset Selector */}
      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom Date Range Picker */}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={preset === 'custom' ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'justify-start text-left font-normal',
              preset === 'custom' && 'bg-primary text-primary-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {preset === 'custom' && customRange
              ? format(customRange.from, 'MMM d') +
                ' - ' +
                format(customRange.to, 'MMM d')
              : 'Custom'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 border-b">
            <p className="text-sm font-medium">Select Date Range</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click two dates to select a range
            </p>
          </div>
          <Calendar
            mode="range"
            defaultMonth={currentRange.from}
            selected={selectedRange}
            onSelect={handleRangeSelect}
            numberOfMonths={2}
          />
          <div className="p-3 border-t flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onCustomRangeChange(undefined);
                if (preset === 'custom') {
                  onPresetChange('this_month');
                }
                setCalendarOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setCalendarOpen(false)}
              disabled={!customRange?.from || !customRange?.to}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-8" />

      {/* Comparison Toggle */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={comparisonEnabled ? 'default' : 'outline'}
            size="sm"
            className={cn(
              comparisonEnabled && 'bg-primary text-primary-foreground'
            )}
          >
            <GitCompare className="mr-2 h-4 w-4" />
            Compare
            <ChevronDown className="ml-2 h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">Period Comparison</h4>
              <p className="text-sm text-muted-foreground">
                Compare metrics with the previous period
              </p>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label htmlFor="comparison-toggle" className="text-sm">
                Enable comparison
              </Label>
              <Switch
                id="comparison-toggle"
                checked={comparisonEnabled}
                onCheckedChange={onComparisonChange}
              />
            </div>
            {comparisonEnabled && (
              <div className="rounded-lg bg-muted/50 p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current:</span>
                  <span className="font-medium">
                    {formatDateRange(currentRange)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Previous:</span>
                  <span className="font-medium">
                    {formatDateRange(previousRange)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
